import React, { useRef, useEffect } from 'react';
import { EditorView } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { basicSetup } from 'codemirror';
import { html } from '@codemirror/lang-html';
import { css } from '@codemirror/lang-css';
import { json } from '@codemirror/lang-json';
import { Box } from '@mui/joy';

interface CodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  language: 'html' | 'css' | 'json';
  height?: string;
  readOnly?: boolean;
}

const languageExtensions = {
  html: () => html(),
  css: () => css(),
  json: () => json(),
};

// Dark theme matching Joy UI dark mode
const darkTheme = EditorView.theme(
  {
    '&': {
      backgroundColor: '#1a1a2e',
      color: '#e0e0e0',
      fontSize: '13px',
      fontFamily: '"Fira Code", "JetBrains Mono", "Consolas", monospace',
    },
    '.cm-content': {
      caretColor: '#528bff',
    },
    '.cm-cursor, .cm-dropCursor': {
      borderLeftColor: '#528bff',
    },
    '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection':
      {
        backgroundColor: '#3d4556',
      },
    '.cm-panels': {
      backgroundColor: '#16213e',
      color: '#e0e0e0',
    },
    '.cm-panels.cm-panels-top': {
      borderBottom: '1px solid #2a2a4a',
    },
    '.cm-panels.cm-panels-bottom': {
      borderTop: '1px solid #2a2a4a',
    },
    '.cm-searchMatch': {
      backgroundColor: '#72a1ff59',
      outline: '1px solid #457dff',
    },
    '.cm-searchMatch.cm-searchMatch-selected': {
      backgroundColor: '#6199ff2f',
    },
    '.cm-activeLine': {
      backgroundColor: '#1e2a3a',
    },
    '.cm-selectionMatch': {
      backgroundColor: '#aafe661a',
    },
    '&.cm-focused .cm-matchingBracket, &.cm-focused .cm-nonmatchingBracket': {
      backgroundColor: '#bad0f847',
    },
    '.cm-gutters': {
      backgroundColor: '#16213e',
      color: '#546e7a',
      border: 'none',
      borderRight: '1px solid #2a2a4a',
    },
    '.cm-activeLineGutter': {
      backgroundColor: '#1e2a3a',
    },
    '.cm-foldPlaceholder': {
      backgroundColor: 'transparent',
      border: 'none',
      color: '#ddd',
    },
    '.cm-tooltip': {
      border: 'none',
      backgroundColor: '#16213e',
    },
    '.cm-tooltip .cm-tooltip-arrow:before': {
      borderTopColor: 'transparent',
      borderBottomColor: 'transparent',
    },
    '.cm-tooltip .cm-tooltip-arrow:after': {
      borderTopColor: '#16213e',
      borderBottomColor: '#16213e',
    },
    '.cm-tooltip-autocomplete': {
      '& > ul > li[aria-selected]': {
        backgroundColor: '#2a2a4a',
        color: '#e0e0e0',
      },
    },
  },
  { dark: true }
);

const CodeEditor: React.FC<CodeEditorProps> = ({
  value,
  onChange,
  language,
  height = '400px',
  readOnly = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);

  // Keep callback ref up-to-date without recreating editor
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Create/destroy editor
  useEffect(() => {
    if (!containerRef.current) return;

    const langExtension = languageExtensions[language];

    // Ensure doc is always a string (json_ld may be an object)
    const docString = typeof value === 'string' ? value : JSON.stringify(value, null, 2);

    const state = EditorState.create({
      doc: docString || '',
      extensions: [
        basicSetup,
        langExtension(),
        darkTheme,
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            onChangeRef.current(update.state.doc.toString());
          }
        }),
        ...(readOnly ? [EditorState.readOnly.of(true)] : []),
        EditorView.lineWrapping,
      ],
    });

    const view = new EditorView({
      state,
      parent: containerRef.current,
    });

    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // Re-create editor only if language or readOnly changes
  }, [language, readOnly]);

  // Update content when value prop changes from outside
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    const currentContent = view.state.doc.toString();
    const newContent = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
    if (currentContent !== newContent) {
      view.dispatch({
        changes: {
          from: 0,
          to: currentContent.length,
          insert: newContent || '',
        },
      });
    }
  }, [value]);

  return (
    <Box
      ref={containerRef}
      sx={{
        height,
        overflow: 'auto',
        border: '1px solid',
        borderColor: 'neutral.outlinedBorder',
        borderRadius: 'sm',
        '& .cm-editor': {
          height: '100%',
        },
        '& .cm-scroller': {
          overflow: 'auto',
        },
      }}
    />
  );
};

export default CodeEditor;
